// Offerten som kunden ser på offertlänken (ADR 0044). Samma data används av
// offertmallen, mejlet och PDF:en. AI:s motiveringar per rad och byråns
// interna uppgifter, som tonen, är inte med.
import { z } from 'zod';
import { billingSchema } from './catalog.js';
import { fontPairSchema, hexColorSchema } from './branding.js';
import { amountSchema, quoteLineSchema } from './quote-calculation.js';

/** De statusar som kunden kan se. Övriga offerter finns inte för kunden. */
export const publicQuoteStatusSchema = z.enum([
  'sent',
  'viewed',
  'accepted',
  'declined',
  'expired',
]);
export type PublicQuoteStatus = z.infer<typeof publicQuoteStatusSchema>;

/** En rad i offerten utan AI:s motivering, som bara byrån ser. */
export const quoteViewLineSchema = quoteLineSchema.omit({ reason: true });
export type QuoteViewLine = z.infer<typeof quoteViewLineSchema>;

export const quoteViewPeriodSchema = z.object({
  billing: billingSchema,
  lines: z.array(quoteViewLineSchema),
  adjustment: z.object({ percent: z.number(), amount: amountSchema }).exactOptional(),
  total: amountSchema,
  vat: amountSchema,
});

const valfri = z.string().nullable();

export const quoteViewSchema = z.object({
  number: z.string(),
  status: publicQuoteStatusSchema,
  /** Dagen offerten skickades, i svensk tid. */
  issuedOn: z.iso.date(),
  validUntil: z.iso.date(),
  firm: z.object({
    name: z.string(),
    orgNumber: z.string(),
    hasFSkatt: z.boolean(),
    accentColor: hexColorSchema,
    fontPair: fontPairSchema,
    introPhrase: valfri,
    notIncluded: valfri,
    terms: valfri,
    footer: valfri,
    contactName: valfri,
    contactTitle: valfri,
    contactEmail: valfri,
    contactPhone: valfri,
    address: valfri,
  }),
  /** Kunden. Saknas när kontaktuppgifterna har gallrats. */
  customer: z.object({ name: z.string(), companyName: valfri }).nullable(),
  introText: valfri,
  /** "Det här har vi utgått från". */
  assumptions: z.array(z.string()),
  periods: z.array(quoteViewPeriodSchema),
  /** Tillval med pris som inte räknas in i summan. */
  optionalLines: z.array(quoteViewLineSchema),
  /** Momssatsen som andel, 0.25 för 25 %. */
  vatRate: z.number(),
  /** Katalogens villkor. */
  catalogTerms: z.array(z.string()),
  /** Byråns länk för att boka uppstartssamtal. */
  bookingUrl: valfri,
  /** När kunden accepterade. Saknas tills dess. */
  acceptedAt: z.iso.datetime().nullable(),
});
export type QuoteView = z.infer<typeof quoteViewSchema>;

export const quoteAcceptanceSchema = z.object({
  status: z.literal('accepted'),
  acceptedAt: z.iso.datetime(),
  bookingUrl: valfri,
});
export type QuoteAcceptance = z.infer<typeof quoteAcceptanceSchema>;
